// istanbul ignore file

import { Button } from '@eventbrite/marmalade';
import { FormattedMessage } from 'react-intl';
import styles from './DefaultFallback.module.scss';

/**
 * This is the default fallback component that is displayed when an error occurs.
 * In production mode, it renders an empty div.
 * In development, it renders a fallback UI with the error message and stack trace.
 */
export const DefaultFallback: React.FC<{ error?: Error; resetError?: () => void }> = ({
    error,
    resetError,
}) => (
    <>
        {process.env.NODE_ENV === 'development' ? (
            <div className={styles.root}>
                <h3 className={styles.title}>Something went wrong</h3>
                <p className={styles.description}>
                    This component encountered an error and could not be displayed. This is a
                    dev-only warning to help debug the error. In production, this component will not
                    be displayed unless a different <code>fallback</code> is provided to the{' '}
                    <code>ErrorBoundary</code> component.
                </p>
                {error && (
                    <div className={styles.errorDetails}>
                        <summary className={styles.errorDetailsSummary}>Error details:</summary>
                        <pre className={styles.errorStack}>
                            {error.message}
                            {error.stack && `\n\n${error.stack}`}
                        </pre>
                    </div>
                )}
                {resetError && (
                    <div className={styles.buttonContainer}>
                        <Button
                            className={styles.retryButton}
                            onClick={resetError}
                            variant="primary"
                        >
                            <FormattedMessage id="Try again" defaultMessage="Try again" />
                        </Button>
                    </div>
                )}
            </div>
        ) : (
            <div />
        )}
    </>
);
